<script setup>
import { isValid } from "date-fns";
import _ from "lodash";
import { computed, defineProps, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import SessionGridRow from "@/components/ExperiencePage/Sessions/Components/SessionGridRow.vue";
import SkeletonLoader from "@/components/shared/loaders/BaseSkeletonLoader.vue";
import { parseSessionStartDate } from "@/mixins/helpers";
import { pastSessionDeadline } from "@/util/pastSessionDeadline";

const emit = defineEmits(["applyClicked", "programdates-set-session-id"]);
const router = useRouter();
const route = useRoute();
const today = new Date();
const selectedYear = ref(today.getFullYear());
const items = ref([]);
const isPages = ref(false);

const mainCardDiv = ref(null);

const pageParam = ref(router?.currentRoute?.value?.query?.page ?? 1);

const chunkedSessions = ref([]);

const loading = computed(() => {
  return props.sessions.length === 0;
});
const props = defineProps({
  sessions: {
    type: Object,
    default() {
      return [];
    },
  },
  travelDetails: {
    type: Object,
    default() {
      return [];
    },
  },
  isLoading: {
    type: Boolean,
    default: true,
  },
  sessionId: {
    type: String,
    default: "",
  },
  programPage: {
    type: Object,
    default() {
      return {};
    },
  },
  externalLink: {
    type: String,
    default: "",
  },
  registerType: {
    type: String,
    default: "",
  },
});

const listOfYearFilters = computed(() => {
  if (!props.sessions) {
    return [];
  }
  const listOfYears = [];
  for (const session of props.sessions) {
    if (session?.session_travel_details?.length > 0) {
      const startDate = new parseSessionStartDate(session?.session_travel_details);
      if (isValid(startDate) && listOfYears.indexOf(startDate.getUTCFullYear()) === -1)
        listOfYears.push(startDate.getUTCFullYear());
    }
  }
  return listOfYears.sort();
});

const filteredSessions = computed(() => {
  const userSelectedSessions = props.sessions.filter((session) =>
    applyUserSelectionSessionFilter(session, selectedYear.value),
  );
  const sessionsNotPastDeadline = userSelectedSessions.filter((sessionObj) => {
    if (!pastSessionDeadline(sessionObj)) return sessionObj;
  });
  return sessionsNotPastDeadline;
});

const newDesignFilteredSessions = computed(() => {
  const newSessions = props.sessions.filter((session) => applyUserSelectionSessionFilter(session, selectedYear.value));
  return newSessions;
});

watch(filteredSessions, async () => {
  chunkedSessions.value = _.chunk(filteredSessions.value, 3);
  if (pageParam.value > chunkedSessions.value.length) {
    pageParam.value = 1;
    await router.push({ query: { ...route.query, page: pageParam.value }, hash: route.hash });
  }
  if (isNaN(pageParam.value)) {
    pageParam.value = 1;
    await router.push({ query: { ...route.query, page: pageParam.value }, hash: route.hash });
  }
  isPages.value = pageParam.value >= chunkedSessions.value.length ? true : false;
  items.value = _.flatten(_.slice(chunkedSessions.value, 0, pageParam.value));
});

const arrivalYear = ref();

watch(
  () => props?.travelDetails,
  (newTravelDetails) => {
    const startDate = parseSessionStartDate(newTravelDetails);
    if (startDate) {
      arrivalYear.value = Number(startDate?.toISOString().substring(0, 4));
    }
  },
  {
    deep: true,
    immediate: true,
  },
);

watch([listOfYearFilters, arrivalYear], () => {
  if (arrivalYear.value && listOfYearFilters.value.includes(arrivalYear.value)) selectedYear.value = arrivalYear.value;
  else if (listOfYearFilters.value) selectedYear.value = listOfYearFilters.value[0];
});

function onClickYearFilter(event) {
  selectedYear.value = new Date(event.target.innerText, 1).getFullYear();
}

function applyUserSelectionSessionFilter(session, userSelectedYear) {
  const applicationDeadline = new Date(parseSessionStartDate(session?.session_travel_details));
  return userSelectedYear === applicationDeadline.getUTCFullYear();
}
</script>
<template>
  <div class="md:pt-20 px-4 md:px-8">
    <div ref="mainCardDiv" class="flex flex-row items-end">
      <h2 class="text-4xl font-bold text-indigo-base">
        <div class="text-2xl">Program</div>
        <div>DATES</div>
      </h2>
      <SkeletonLoader v-if="loading" :height="'h-13'" class="mb-4 ml-14" rounded />
      <div v-if="!loading" class="hidden lg:flex flex-row ml-auto">
        <div
          v-for="year in listOfYearFilters"
          :id="`div-${year}`"
          :key="`key-${year}`"
          :onClick="onClickYearFilter"
          class="box-border m-2 border-2 px-10 py-3 cursor-pointer font-bold text-base"
          :class="
            year === selectedYear
              ? secondaryColor
                ? `${primaryColorClassBG} text-white`
                : 'bg-indigo-base text-white hover:bg-indigo-base hover:text-white'
              : secondaryColor
              ? `${primaryColorClassOutlined}`
              : 'text-indigo-base hover:bg-indigo-base hover:text-white'
          "
        >
          <p :id="`${year}-paragraph`">
            {{ year }}
          </p>
        </div>
      </div>
    </div>
    <div class="flex-row mt-4 mb-4">
      <p class="text-lg font-montserrat text-university-primary">
        Pick a date below to see more information about your program. When you make a selection, the information on the
        page will update automatically to reflect your choice.
      </p>
    </div>
    <div v-if="!loading" class="lg:hidden flex flex-col mt-6">
      <div class="flex flex-row flex-wrap justify-start ml-auto">
        <div
          v-for="year in listOfYearFilters"
          :id="`div-${year}-mobile`"
          :key="`key-${year}-mobile`"
          :onClick="onClickYearFilter"
          class="box-border m-2 border-2 px-10 py-3 cursor-pointer font-bold text-base"
          :class="
            year === selectedYear
              ? secondaryColor
                ? `${primaryColorClassBG} text-white`
                : 'bg-indigo-base text-white hover:bg-indigo-base hover:text-white'
              : secondaryColor
              ? `${primaryColorClassOutlined}`
              : 'text-indigo-base hover:bg-indigo-base hover:text-white'
          "
        >
          <p :id="`${year}-mobile`">
            {{ year }}
          </p>
        </div>
      </div>
    </div>
    <SessionGridRow
      v-for="(session, index) in newDesignFilteredSessions"
      :key="session.id"
      :index-number="index"
      :session="session"
      :selected-session-id="props.sessionId ?? ''"
      :oddrow="index % 2 === 0"
      :display-starting-price="props.programPage?.display_starting_price"
      :external-link="props.externalLink"
      :register-type="props.registerType"
      @dategridrow-set-session-id="(sessionId) => emit('programdates-set-session-id', sessionId)"
      @applyBtnClicked="emit('applyClicked')"
    />
    <hr class="my-1 md:mt-20 border-b-3 border-gray-200" />
  </div>
</template>
